import { hasCurrentUserReviewedVendor } from '@api/reviews';
import { isInNewCTAExperiment } from '@client/redux/experiments/selectors/new-cta';
import {
	hasCurrentUserReviewedAtom,
	ratingAtom,
	reviewStarBucketsAtom,
	totalCountAtom,
} from '@components/reviews/atoms';
import { filterByReviewBucketAtom } from '@components/reviews/components/search-sort-filter/atoms';
import { useLiteStorefront } from '@hooks/use-lite-storefront';
import { useAppSelector } from '@redux/hooks';
import { GridColumn } from '@tkww/orion-web-ds-structure';
import { GridRow } from '@tkww/orion-web-ds-structure';
import { TextBody, TextTitle } from '@tkww/orion-web-ds-text';
import { Logger } from '@utils/logger/logger';
import ProgressBar from '@xo-union/tk-component-progress-bar';
import classNames from 'classnames';
import { useAtomValue, useSetAtom } from 'jotai';
import React, { type FC, useEffect, useMemo } from 'react';
import type VendorAnalytics from '../../../../../../decorators/vendorAnalytics';
import ReviewStars from '../../../vendor_info/components/review_stars';
import { AfterReview } from '../after_review';
import ReviewPrompt from '../review_prompt';
import ReviewAISummary from '../reviews_ai_summary';
import VerifiedReviewsBox from '../verified_reviews_box';
import Styles from './styles.scss';

export interface Props {
	openModal: () => void;
	vendorAnalytics?: VendorAnalytics;
	hasGoogleReviews: boolean;
}

interface ReviewBucketProps {
	stars: number;
	text: '5 Star' | '4 Star' | '3 Star' | '2 Star' | '1 Star';
	progressBarColor?: string | null;
}

const ReviewBucket: FC<ReviewBucketProps> = ({
	stars,
	text,
	progressBarColor = '#FBAF00',
}) => {
	const pct = useMemo(() => `${(stars * 100).toFixed(0)}%`, [stars]);
	const filterByReviewSummary = useSetAtom(filterByReviewBucketAtom);
	const handleClick = () => filterByReviewSummary(text);

	return (
		<button onClick={handleClick} type="button" className={Styles.reviewBucket}>
			<div className={Styles.starText}>{text}</div>
			<div className={Styles.progressBarColumn}>
				<div className={Styles.progressBarWrapper}>
					<ProgressBar progress={stars} color={progressBarColor} />
				</div>
			</div>
			<div className={Styles.pctText}>{pct}</div>
		</button>
	);
};

type ReviewsSummaryContentProps = Omit<Props, 'hasGoogleReviews'> & {
	progressBarColor?: string;
	shouldHideReviewPrompt: boolean;
};

const ReviewsSummaryContent: FC<ReviewsSummaryContentProps> = ({
	openModal,
	progressBarColor,
	vendorAnalytics,
	shouldHideReviewPrompt,
}) => {
	const isLiteStorefront = useLiteStorefront();
	const totalVendorReviewsCount = useAtomValue(totalCountAtom);
	const rating = useAtomValue(ratingAtom);
	const buckets = useAtomValue(reviewStarBucketsAtom);
	const inCTAExperiment = useAppSelector(isInNewCTAExperiment);

	return (
		<>
			<GridColumn className={Styles.reviewDetailsItem} xs="12" sm="6" md="6">
				<div className={Styles.reviewDetailsHeader}>
					<div className={Styles.reviewDetailsRating}>
						<TextBody size="md" data-testid="review-rating">
							<TextTitle size="md" className={Styles.reviewsDetailsStarCount}>
								{!isLiteStorefront ? rating.toFixed(1) : rating}
							</TextTitle>{' '}
							out of 5.0
						</TextBody>
						<ReviewStars overallRating={rating} size="lg" />
					</div>
					<TextBody size="md" className={Styles.reviewsCountText}>
						{totalVendorReviewsCount} reviews
					</TextBody>
				</div>
				{inCTAExperiment ? (
					<AfterReview alignment="left" border="none" mobileBorder="partial" />
				) : !shouldHideReviewPrompt ? (
					<ReviewPrompt
						handleClick={openModal}
						vendorAnalytics={vendorAnalytics}
					/>
				) : null}
			</GridColumn>
			<GridColumn className={Styles.reviewDetailsBuckets} xs="12" sm="6" md="6">
				<ReviewBucket
					stars={buckets.stars5}
					text="5 Star"
					progressBarColor={progressBarColor}
				/>
				<ReviewBucket
					stars={buckets.stars4}
					text="4 Star"
					progressBarColor={progressBarColor}
				/>
				<ReviewBucket
					stars={buckets.stars3}
					text="3 Star"
					progressBarColor={progressBarColor}
				/>
				<ReviewBucket
					stars={buckets.stars2}
					text="2 Star"
					progressBarColor={progressBarColor}
				/>
				<ReviewBucket
					stars={buckets.stars1}
					text="1 Star"
					progressBarColor={progressBarColor}
				/>
			</GridColumn>
		</>
	);
};

const ReviewsSummary: FC<Props> = ({
	openModal,
	vendorAnalytics,
	hasGoogleReviews,
}) => {
	const isLiteStorefront = useLiteStorefront();
	const progressBarColor = !isLiteStorefront ? '#37393F' : undefined;
	const hasCurrentUserReviewed = useAtomValue(hasCurrentUserReviewedAtom);
	const setHasCurrentUserReviewed = useSetAtom(hasCurrentUserReviewedAtom);
	const isLoggedIn = useAppSelector((state) =>
		Boolean(state.membership.member),
	);
	const vendorId = useAppSelector((state) => state.vendor.vendor?.id);
	const shouldHideReviewPrompt = isLoggedIn && hasCurrentUserReviewed;
	const reviewSummaryGenerated = useAppSelector(
		(state) => state.vendor.vendorRaw?.ai?.reviewSummaryGenerated?.summaryText,
	);
	const totalVendorReviewsCount = useAtomValue(totalCountAtom);
	const showVerifiedReviewsMessage = Boolean(totalVendorReviewsCount);
	const shouldShowReviewsAISummary =
		!isLiteStorefront && reviewSummaryGenerated;

	useEffect(() => {
		if (!isLoggedIn || !vendorId || hasCurrentUserReviewed) {
			return;
		}

		let active = true;
		hasCurrentUserReviewedVendor(vendorId)
			.then((hasReviewed) => {
				if (active) {
					setHasCurrentUserReviewed(hasReviewed);
				}
			})
			.catch((error) => {
				if (Array.isArray(error)) {
					return;
				}

				Logger.error({
					log_message:
						'Client error fetching current-user reviewed eligibility',
					errorToLog: error,
					newRelic: true,
				});
			});

		return () => {
			active = false;
		};
	}, [hasCurrentUserReviewed, isLoggedIn, setHasCurrentUserReviewed, vendorId]);

	if (hasGoogleReviews && !totalVendorReviewsCount) {
		return null;
	}

	if (!hasGoogleReviews && !totalVendorReviewsCount) {
		return (
			<GridRow data-testid="reviews-summary-no-reviews">
				<GridColumn xs="12">
					{!shouldHideReviewPrompt ? (
						<ReviewPrompt
							handleClick={openModal}
							vendorAnalytics={vendorAnalytics}
						/>
					) : null}
				</GridColumn>
			</GridRow>
		);
	}

	return (
		<>
			<GridRow className={Styles.reviewDetailRow}>
				{!isLiteStorefront ? (
					<div
						className={classNames(Styles.reviewDetailWrapper, {
							[Styles.reviewDetailWrapperComplete]: !showVerifiedReviewsMessage,
						})}
					>
						<ReviewsSummaryContent
							openModal={openModal}
							progressBarColor={progressBarColor}
							vendorAnalytics={vendorAnalytics}
							shouldHideReviewPrompt={shouldHideReviewPrompt}
						/>
					</div>
				) : (
					<ReviewsSummaryContent
						openModal={openModal}
						progressBarColor={progressBarColor}
						vendorAnalytics={vendorAnalytics}
						shouldHideReviewPrompt={shouldHideReviewPrompt}
					/>
				)}
				{showVerifiedReviewsMessage && !isLiteStorefront && (
					<VerifiedReviewsBox />
				)}
			</GridRow>

			{shouldShowReviewsAISummary && (
				<ReviewAISummary reviewSummary={reviewSummaryGenerated} />
			)}
		</>
	);
};

export default ReviewsSummary;
